/* ============================================================
   Freshé WCAG 2.2 AA remediation — runtime pass (Sep 2 2026)
   Loaded deferred from layout/theme.liquid.

   Covers findings that live in markup repeated across many
   sections (pasted SVG exports, link-as-button controls, injected
   accordion headings). Doing this once here, with a MutationObserver
   for content injected later (product detail panels, cart drawer
   re-renders), is more robust than hand-editing every section.
   ============================================================ */
(function () {
  'use strict';

  /* --- 4.1.1 / 4.1.2 Duplicate IDs on inline SVG exports -----------
     Illustrator/XD exports (Grupo_135, Icon_awesome-instagram, …) are
     pasted verbatim in header, footer, cart drawer and product grids.
     The ids on <g> and <path> nodes are never referenced, so they can
     be dropped. Definition elements (pattern, gradients, masks, clip
     paths, filters, symbols, markers) ARE referenced — via href="#id"
     or fill/stroke/mask/clip-path/filter="url(#id)" — and must keep
     theirs, otherwise the artwork that uses them goes blank. */
  var SVG_DEFINITION_TAGS = { symbol: 1, pattern: 1, lineargradient: 1, radialgradient: 1, mask: 1, clippath: 1, filter: 1, marker: 1, defs: 1 };

  /* Performance note (Sep 3 2026): the first version tested each id with
     ~13 document-wide querySelector calls, for every id, on every DOM
     mutation — ~2.5 s of main-thread time on a phone (Lighthouse). Now:
     one pass collects every id the document references (href="#x",
     url(#x), aria-labelledby/-describedby), then unreferenced ids are
     stripped. Each <svg> is marked so it is never rescanned. */
  var REF_ATTR_SELECTOR = '[href^="#"], [xlink\\:href^="#"], [aria-labelledby], [aria-describedby], ' +
    '[fill*="url("], [stroke*="url("], [mask*="url("], [clip-path*="url("], [filter*="url("], ' +
    '[marker-start*="url("], [marker-mid*="url("], [marker-end*="url("], [style*="url("]';
  var REF_ATTRS = ['href', 'xlink:href', 'aria-labelledby', 'aria-describedby', 'fill', 'stroke', 'mask',
    'clip-path', 'filter', 'marker-start', 'marker-mid', 'marker-end', 'style'];
  function collectReferencedIds() {
    var ids = {};
    var els = document.querySelectorAll(REF_ATTR_SELECTOR);
    for (var i = 0; i < els.length; i++) {
      for (var a = 0; a < REF_ATTRS.length; a++) {
        var v = els[i].getAttribute(REF_ATTRS[a]);
        if (!v) continue;
        if (REF_ATTRS[a] === 'href' || REF_ATTRS[a] === 'xlink:href') {
          if (v.charAt(0) === '#') ids[v.slice(1)] = 1;
        } else if (REF_ATTRS[a] === 'aria-labelledby' || REF_ATTRS[a] === 'aria-describedby') {
          var parts = v.split(/\s+/);
          for (var p = 0; p < parts.length; p++) if (parts[p]) ids[parts[p]] = 1;
        } else {
          var re = /url\(\s*['"]?#([^'")\s]+)/g, m;
          while ((m = re.exec(v))) ids[m[1]] = 1;
        }
      }
    }
    return ids;
  }
  function dedupeSvgIds(root) {
    var scope = root || document;
    var svgs = scope.querySelectorAll ? scope.querySelectorAll('svg:not([data-a11y-ids])') : [];
    if (scope.tagName === 'svg' && !scope.hasAttribute('data-a11y-ids')) svgs = [scope];
    if (!svgs.length) return;
    var referenced = collectReferencedIds();
    for (var s = 0; s < svgs.length; s++) {
      var svg = svgs[s];
      svg.setAttribute('data-a11y-ids', '1');
      var nodes = svg.querySelectorAll('[id]');
      var list = [];
      if (svg.hasAttribute('id')) list.push(svg);
      for (var n = 0; n < nodes.length; n++) list.push(nodes[n]);
      for (var i = 0; i < list.length; i++) {
        var el = list[i];
        var tag = el.tagName ? el.tagName.toLowerCase() : '';
        if (SVG_DEFINITION_TAGS[tag]) continue;
        if (el.closest && el.closest('defs')) continue;
        var id = el.getAttribute('id');
        if (!id || referenced[id]) continue;
        el.removeAttribute('id');
      }
    }
  }

  /* --- 1.1.1 / 4.1.2 Decorative SVGs inside already-labelled controls --
     A link or button that carries aria-label (or visually-hidden text)
     should not also expose its icon to assistive tech. */
  function hideDecorativeSvgs(root) {
    var svgs = (root || document).querySelectorAll('a[aria-label] svg, button[aria-label] svg, .drawer__close svg, .newsletter-form__button svg, .btnaddproductdrawer svg');
    for (var i = 0; i < svgs.length; i++) {
      var s = svgs[i];
      if (!s.hasAttribute('aria-hidden')) s.setAttribute('aria-hidden', 'true');
      if (!s.hasAttribute('focusable')) s.setAttribute('focusable', 'false');
    }
  }

  /* --- 4.1.2 Dialogs without a usable accessible name ---------------
     Shopify's Customer Privacy banner (#shopify-pc__banner) is injected
     with role="dialog". It keeps failing the dialog-name audit even with
     an aria-label applied on insertion, which points to Shopify setting
     aria-labelledby to a target that is missing or empty (that overrides
     aria-label). Resolve the name the way axe does; if it comes back
     empty, remove the broken reference and label the dialog directly. */
  function labelledbyText(el) {
    var ids = (el.getAttribute('aria-labelledby') || '').split(/\s+/).filter(Boolean);
    var text = '';
    for (var i = 0; i < ids.length; i++) {
      var t = document.getElementById(ids[i]);
      if (t) text += ' ' + (t.textContent || t.getAttribute('aria-label') || '');
    }
    return text.replace(/\s+/g, ' ').trim();
  }
  function nameDialogs() {
    var dialogs = document.querySelectorAll('[role="dialog"], [role="alertdialog"]');
    for (var i = 0; i < dialogs.length; i++) {
      var d = dialogs[i];
      var label = (d.getAttribute('aria-label') || '').trim();
      var byText = d.hasAttribute('aria-labelledby') ? labelledbyText(d) : '';
      if (d.hasAttribute('aria-labelledby') && !byText) d.removeAttribute('aria-labelledby');
      if (byText || label) continue;
      var name = '';
      if (d.id === 'shopify-pc__banner' || d.classList.contains('shopify-pc__banner__dialog')) {
        name = 'Cookie and privacy preferences';
      } else {
        var h = d.querySelector('h1, h2, h3, h4, [class*="heading"], [class*="title"]');
        name = h ? (h.textContent || '').replace(/\s+/g, ' ').trim() : '';
      }
      if (name) d.setAttribute('aria-label', name);
    }
  }

  /* --- 4.1.2 Frames without a title ------------------------------------
     Shopify's preview bar (iframe#PBarNextFrame) and some app embeds inject
     untitled iframes. The preview bar only exists in theme preview, but a
     generic fallback keeps future app iframes from failing the same audit. */
  function titleFrames() {
    var frames = document.querySelectorAll('iframe:not([title]), iframe[title=""]');
    for (var i = 0; i < frames.length; i++) {
      var f = frames[i];
      var name = '';
      if (f.id === 'PBarNextFrame' || /preview/i.test(f.id + ' ' + f.className)) name = 'Shopify theme preview bar';
      else if (f.getAttribute('aria-label')) name = f.getAttribute('aria-label');
      else if (f.name) name = f.name.replace(/[-_]+/g, ' ');
      else name = 'Embedded content';
      f.setAttribute('title', name);
    }
  }

  /* --- WebMCP declarative form annotations (Agentic Browsing) ---------
     Shopify already registers the imperative WebMCP tools (search_catalog,
     update_cart, …). This is the complementary declarative layer: the
     <form> itself carries toolname/tooldescription and its meaningful
     inputs carry toolparamdescription, so a browser agent reading the DOM
     can identify each form without guessing at field names. Applied at
     runtime so it survives the cart drawer's section re-renders. The
     attributes are inert for browsers and users. */
  var FORM_TOOLS = [
    {
      match: '#CartDrawer-Form, form.cart-drawer__form, form#cart.cart__contents',
      name: 'update_cart_form',
      description: 'Shopping cart form. Change the quantity of any line item (set 0 to remove it), add an order note, then submit to proceed to checkout.',
      params: [
        { match: 'input[name="updates[]"]', description: 'New absolute quantity for this cart line item; 0 removes the item.' },
        { match: 'textarea[name="note"]', description: 'Optional order note or special instructions for the merchant.' },
        { match: 'button[name="checkout"]', description: 'Submit the cart and proceed to checkout.' }
      ]
    },
    {
      match: 'form.search-modal__form, form.search[action*="/search"], form[role="search"]',
      name: 'search_catalog_form',
      description: 'Site search. Enter a query to find Freshé and Henry & Lisa\'s products, pages and articles.',
      params: [
        { match: 'input[type="search"], input[name="q"]', description: 'Search query text, e.g. a product name, flavour or ingredient.' }
      ]
    },
    {
      match: '#ContactFooter, form.footer__newsletter, form.newsletter-form',
      name: 'newsletter_signup_footer',
      description: 'Subscribe to the Freshé email newsletter (footer). Submit an email address to join the mailing list.',
      params: [
        { match: 'input[type="email"], input[name="contact[email]"]', description: 'Subscriber email address.' }
      ]
    },
    {
      match: '#newsletter-form, form.newsletter-modal__form',
      name: 'newsletter_signup_popup',
      description: 'Subscribe to the Freshé email newsletter (pop-up). Submit an email address to join the mailing list.',
      params: [
        { match: 'input[type="email"], input[name="contact[email]"]', description: 'Subscriber email address.' }
      ]
    }
  ];
  function annotateForms() {
    for (var i = 0; i < FORM_TOOLS.length; i++) {
      var t = FORM_TOOLS[i];
      var forms = document.querySelectorAll(t.match);
      for (var f = 0; f < forms.length; f++) {
        var form = forms[f];
        if (form.getAttribute('toolname') !== t.name) {
          form.setAttribute('toolname', t.name);
          form.setAttribute('tooldescription', t.description);
        }
        for (var p = 0; p < t.params.length; p++) {
          var fields = form.querySelectorAll(t.params[p].match);
          for (var k = 0; k < fields.length; k++) {
            if (!fields[k].hasAttribute('toolparamdescription')) {
              fields[k].setAttribute('toolparamdescription', t.params[p].description);
            }
          }
        }
      }
    }
  }

  /* --- Mobile menu drawer: clean state across navigations ----------------
     Dawn's header drawer is a <details> whose open state, submenu state and
     entrance animation can be restored by Safari's back/forward cache or
     left half-applied when a menu link navigates mid-animation. The symptom
     is a drawer that opens showing only the utility links (Log in, socials)
     with the navigation list blank. Reset the drawer to fully closed on
     every page show, and close any stale submenu when the hamburger is
     tapped, so the open animation starts from a known state. */
  var DRAWER = '#Details-menu-drawer-container';
  function resetMenuDrawer() {
    var d = document.querySelector(DRAWER);
    if (!d) return;
    var subs = d.querySelectorAll('details');
    for (var i = 0; i < subs.length; i++) {
      subs[i].classList.remove('menu-opening');
      if (subs[i].hasAttribute('open')) subs[i].removeAttribute('open');
    }
    d.classList.remove('menu-opening');
    if (d.hasAttribute('open')) d.removeAttribute('open');
    var summary = d.querySelector('summary');
    if (summary) summary.setAttribute('aria-expanded', 'false');
    document.body.classList.remove('overflow-hidden-mobile', 'overflow-hidden-tablet', 'overflow-hidden-desktop', 'overflow-hidden');
    var container = document.querySelector('.menu-drawer-container');
    if (container) container.classList.remove('menu-opening');
  }
  window.addEventListener('pageshow', function (e) { if (e.persisted) resetMenuDrawer(); });
  window.addEventListener('pagehide', function () { resetMenuDrawer(); });
  document.addEventListener('click', function (e) {
    var s = e.target.closest && e.target.closest(DRAWER + ' > summary');
    if (!s) return;
    var d = s.parentElement;
    if (d && !d.hasAttribute('open')) {
      // Opening: make sure no stale submenu is left "open" from a previous page.
      var subs = d.querySelectorAll('details[open], details.menu-opening');
      for (var i = 0; i < subs.length; i++) { subs[i].classList.remove('menu-opening'); subs[i].removeAttribute('open'); }
    }
  }, true);

  /* --- Testimonials carousel: dots instead of arrows ---------------------
     The review sliders (.c_content_slider_reviews / _henry) are initialised
     by the grid code with arrows and no dots. The arrows collide with the
     review screenshots, so once Slick is up, switch the live instance to
     dots + no arrows (slickSetOption with refresh) and let CSS place the
     dots under the images. */
  function reviewSliderDots() {
    if (!window.jQuery || !jQuery.fn || !jQuery.fn.slick) return false;
    var done = true;
    jQuery('.c_content_slider_reviews .slick-initialized, .c_content_slider_reviews_henry .slick-initialized').each(function () {
      var $s = jQuery(this);
      if ($s.data('a11yDots')) return;
      try {
        $s.slick('slickSetOption', { arrows: false, dots: true }, true);
        $s.data('a11yDots', true);
      } catch (e) { done = false; }
    });
    return done;
  }
  (function pollReviewSliders() {
    var tries = 0;
    var t = setInterval(function () {
      tries++;
      var any = document.querySelector('.c_content_slider_reviews, .c_content_slider_reviews_henry');
      if (!any || tries > 100) { clearInterval(t); return; }
      if (any.querySelector('.slick-initialized') && reviewSliderDots()) { clearInterval(t); tagReviewImages(); }
    }, 150);
  })();

  /* --- Testimonials: tap to enlarge ------------------------------------
     The review screenshots are small on phones. Tapping one opens it in a
     simple lightbox (near full-width, dark backdrop, pinch-zoomable), closed
     by tapping anywhere or pressing Escape. Keyboard: images are given a
     button role so Enter/Space open them too. */
  var LB_IMG = '.c_content_slider_reviews .slick-slide:not(.slick-cloned) img, .c_content_slider_reviews_henry .slick-slide:not(.slick-cloned) img';
  var lightbox = null;
  function openLightbox(src, alt) {
    if (!lightbox) {
      lightbox = document.createElement('div');
      lightbox.className = 'rv-lightbox';
      lightbox.setAttribute('role', 'dialog');
      lightbox.setAttribute('aria-modal', 'true');
      lightbox.setAttribute('aria-label', 'Enlarged review');
      lightbox.innerHTML = '<button type="button" class="rv-lightbox__close" aria-label="Close">&times;</button><img alt="">';
      lightbox.addEventListener('click', closeLightbox);
      document.body.appendChild(lightbox);
    }
    var img = lightbox.querySelector('img');
    img.src = src;
    img.alt = alt || 'Customer review';
    lightbox.classList.add('is-open');
    document.body.classList.add('rv-lightbox-open');
    lightbox.querySelector('.rv-lightbox__close').focus();
  }
  function closeLightbox() {
    if (!lightbox) return;
    lightbox.classList.remove('is-open');
    document.body.classList.remove('rv-lightbox-open');
  }
  document.addEventListener('click', function (e) {
    var img = e.target.closest && e.target.closest(LB_IMG);
    if (!img) return;
    e.preventDefault();
    openLightbox(img.currentSrc || img.src, img.alt);
  });
  document.addEventListener('keydown', function (e) {
    if ((e.key === 'Escape' || e.keyCode === 27) && lightbox && lightbox.classList.contains('is-open')) closeLightbox();
  });
  function tagReviewImages() {
    var imgs = document.querySelectorAll(LB_IMG);
    for (var i = 0; i < imgs.length; i++) {
      var im = imgs[i];
      if (im.getAttribute('data-lb') === '1') continue;
      im.setAttribute('data-lb', '1');
      im.setAttribute('role', 'button');
      im.setAttribute('tabindex', '0');
      if (!im.getAttribute('alt')) im.setAttribute('alt', 'Customer review');
      im.setAttribute('aria-label', 'Enlarge review');
    }
  }

  /* --- PDP titles: keep "Variety Pack" together on its own line ----------
     "Freshé Tuna Variety Pack" was breaking as three words + "Pack" alone.
     Insert a line break before "Variety Pack" so the title reads as two
     balanced lines. Idempotent: skips headings already processed. */
  function wrapVarietyPack() {
    var heads = document.querySelectorAll('#c_detail_product #column_content h2, #c_detail_product_henry #column_content_henry h2, .product__title h1, .product__title h2');
    for (var i = 0; i < heads.length; i++) {
      var h = heads[i];
      if (h.getAttribute('data-vp') === '1') continue;
      if (h.children.length) continue;                 /* plain text only */
      var m = /^(.*\S)\s+(Variety\s+Pack)\s*$/i.exec(h.textContent || '');
      if (!m) continue;
      h.textContent = '';
      h.appendChild(document.createTextNode(m[1]));
      h.appendChild(document.createElement('br'));
      h.appendChild(document.createTextNode(m[2]));
      h.setAttribute('data-vp', '1');
    }
  }

  /* --- TINNED GOLD card: stack the heading on two lines explicitly ----
     A CSS min-content width let Dawn's overflow-wrap break the heading
     between letters. Put a hard <br> between the two words instead. */
  function stackTinnedGold() {
    var heads = document.querySelectorAll('[id$="__image_banner_nnmU7V"] .banner__box h2');
    for (var i = 0; i < heads.length; i++) {
      var h = heads[i];
      if (h.getAttribute('data-stacked') === '1' || h.querySelector('br')) continue;
      var words = (h.textContent || '').trim().split(/\s+/);
      if (words.length !== 2) continue;
      h.textContent = '';
      h.appendChild(document.createTextNode(words[0]));
      h.appendChild(document.createElement('br'));
      h.appendChild(document.createTextNode(words[1]));
      h.setAttribute('data-stacked', '1');
    }
  }

  /* --- 2.1.1 / 4.1.2 Links that behave as buttons -----------------------
     Quick-add and add-to-cart controls are <a href=""> with a click
     handler. Give them a button role and make Space activate them like a
     real button (Enter already works on an anchor with href). */
  var BUTTON_LINKS = '.add_product, .add_product_henry, .btnaddproduct, .btnaddproduct_henry, .btnaddproductdrawer, #btnshop';
  function roleButtonLinks(root) {
    var links = (root || document).querySelectorAll(BUTTON_LINKS);
    for (var i = 0; i < links.length; i++) {
      var a = links[i];
      if (a.classList.contains('sold_out_product') || a.classList.contains('disabled_sold')) {
        a.setAttribute('role', 'button');
        a.setAttribute('aria-disabled', 'true');
        continue;
      }
      if (!a.hasAttribute('role')) a.setAttribute('role', 'button');
      if (!a.hasAttribute('href') || a.getAttribute('href') === '') a.setAttribute('href', '#');
    }
  }

  /* --- 2.1.1 / 4.1.2 Product-detail accordion headings -------------------
     The product accordion is injected as <h4> click targets. Promote each
     to a keyboard-operable disclosure: focusable, role=button, aria-expanded
     kept in sync with the existing .active class. */
  var ACCORDION_H4 = '.pcontent_accordeon h4, .pcontent_henry_accordeon h4';
  function upgradeAccordions(root) {
    var heads = (root || document).querySelectorAll(ACCORDION_H4);
    for (var i = 0; i < heads.length; i++) {
      var h = heads[i];
      if (h.getAttribute('data-a11y') === '1') continue;
      h.setAttribute('data-a11y', '1');
      h.setAttribute('role', 'button');
      h.setAttribute('tabindex', '0');
      h.setAttribute('aria-expanded', h.classList.contains('active') ? 'true' : 'false');
    }
  }
  function syncAccordionState() {
    var heads = document.querySelectorAll(ACCORDION_H4 + '[data-a11y="1"]');
    for (var i = 0; i < heads.length; i++) {
      heads[i].setAttribute('aria-expanded', heads[i].classList.contains('active') ? 'true' : 'false');
    }
  }

  /* --- 2.1.1 Born of Adventure carousel tiles ----------------------------
     Active tile is a clickable <div>. Make it reachable and operable. */
  function upgradeCarouselTiles() {
    var tiles = document.querySelectorAll('#born_of_adventure .view_product2');
    for (var i = 0; i < tiles.length; i++) {
      var t = tiles[i];
      var active = t.classList.contains('active');
      t.setAttribute('role', 'button');
      t.setAttribute('tabindex', active ? '0' : '-1');
      if (!t.hasAttribute('aria-label') && t.getAttribute('data-title')) {
        t.setAttribute('aria-label', 'View ' + t.getAttribute('data-title'));
      }
    }
  }

  /* Keyboard: Space/Enter on role=button elements that are not native buttons */
  document.addEventListener('keydown', function (e) {
    var key = e.key || e.keyCode;
    var isEnter = key === 'Enter' || key === 13;
    var isSpace = key === ' ' || key === 'Spacebar' || key === 32;
    if (!isEnter && !isSpace) return;
    var el = e.target;
    if (!el || el.tagName === 'BUTTON' || el.tagName === 'INPUT' || el.tagName === 'SELECT' || el.tagName === 'TEXTAREA') return;
    if (el.getAttribute('role') !== 'button') return;
    if (el.getAttribute('aria-disabled') === 'true') { e.preventDefault(); return; }
    // Anchors already fire click on Enter; only intercept Space for them.
    if (el.tagName === 'A' && isEnter) return;
    e.preventDefault();
    el.click();
  });

  /* Accordion click → keep aria-expanded in sync (the theme toggles .active) */
  document.addEventListener('click', function (e) {
    var h = e.target.closest && e.target.closest(ACCORDION_H4);
    if (h) setTimeout(syncAccordionState, 0);
  });

  /* --- 4.1.2 Header account link: let the visually-hidden text be the name */
  function fixHeaderLabels() {
    var acct = document.querySelector('.header__icon--account[aria-label="Customer"]');
    if (acct) acct.removeAttribute('aria-label');
  }

  function runAll(root) {
    dedupeSvgIds(root);
    hideDecorativeSvgs(root);
    nameDialogs();
    titleFrames();
    annotateForms();
    wrapVarietyPack();
    stackTinnedGold();
    tagReviewImages();
    roleButtonLinks(root);
    upgradeAccordions(root);
    upgradeCarouselTiles();
    fixHeaderLabels();
  }

  /* Re-run only for inserted content (product detail panels, cart drawer
     re-renders, app embeds). Attribute changes are deliberately NOT
     observed: Slick and Dawn toggle classes constantly, and this script's
     own setAttribute calls would otherwise re-trigger it in a loop. Work is
     batched and deferred to idle time so it never competes with paint. */
  var pending = [];
  var scheduled = false;
  var idle = window.requestIdleCallback || function (cb) { return setTimeout(function () { cb({ timeRemaining: function () { return 50; } }); }, 200); };
  function flush() {
    scheduled = false;
    var roots = pending; pending = [];
    var sawElement = false;
    for (var i = 0; i < roots.length; i++) {
      if (roots[i].nodeType === 1 && document.contains(roots[i])) { sawElement = true; break; }
    }
    if (!sawElement) return;
    runAll();
  }
  function schedule(mutations) {
    for (var m = 0; m < mutations.length; m++) {
      var added = mutations[m].addedNodes;
      for (var n = 0; n < added.length; n++) {
        if (added[n].nodeType === 1 && !(lightbox && lightbox.contains(added[n]))) pending.push(added[n]);
      }
    }
    if (scheduled || !pending.length) return;
    scheduled = true;
    idle(flush);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { runAll(); });
  } else {
    runAll();
  }
  window.addEventListener('load', function () { idle(function () { runAll(); }); });

  new MutationObserver(schedule).observe(document.documentElement, {
    subtree: true,
    childList: true
  });
})();
